// docs/21 §7, docs/22 O4 the intro: the World's cold open, shot like a film. Letterbox bars, a slow
// camera drift over each painted scene and a cut through black between them. The story's panels as
// numbered chapter cards (the heading, the narration typed out, then the spoken line in a dialogue
// box with the speaker's seal), the first carrying the realm's title over it (docs/133 YA7), the reveal
// of the first named foe over the battle's own ground, hour and weather, and the call to arms: the
// first battle's card with the player's heroes standing on that ground. Click / Space / Enter
// finishes the line, then turns the page; ← goes back. "To arms" is `opening_ready`; the battle
// starts when everyone still watching has taken up arms (the keeper may sound the horn).
//
// Pages turn through one curtain: it falls to black, the page swaps underneath, the new scene
// paints, and only then does it rise, so no frame of the next scene shows before the fade. Clicks
// while it is falling retarget the turn rather than restarting it; a page already read comes back
// whole. Each panel's backdrop is blocked from its own words by the story lexicon.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { OFFLINE_TITLE, useLinkUp } from '../../../core/stores/link';
import { Button, CINE_TYPE_GAIN, HeroSprite, Modal, SightLayer, motionMs, useBeatScope, useBeats, useTypewriter } from '../../components';
import { useSession } from '../../../core/stores/session';
import { useRoute } from '../../../core/stores/route';
import { rpc } from '../../../core/net/reducers';
import { identityHex, tsToMs } from '../../../core/net/connection';
import { serverNow } from '../../../core/time';
import { CLASS_NAMES } from '../../../core/sim/adapt';
import { classLook, heroToken, type HeroLook } from '../../../game/heroKit';
import { useHeroLooks } from '../../../core/heroes/looks';
import { useGenesis, useMyHeroes, useNames, useWorld, useWorldMembers, useWorldOpening, useWorldPresence } from '../../../core/subscriptions/hooks';
import { display, OPENING, placeOf, storyDate, useStoryMarks, withHeroes, type Genesis, type People, type Scene, type Stage } from '../../../core/story/genesis';
import { bandFrames, briefFoeWords, castPanel, leaderFrame, peopleOf, PEOPLE_LOOK, storyContext, textSeed, type Cast } from '../../../core/story/lexicon';
import { beatsFor } from '../../../core/story/cues';
import { ASH_SKY_WORDS, DEEP_SKY_WORDS } from '../../../core/story/calendar';
import { useDialogs } from '../../components/Modal';
import { HeroForge } from '../HeroForge';
import { useFounding } from '../Outfitting';
import { SceneBackdrop } from './Scene';
import './intro.css';
import { playSfx, preloadSfx } from '../../../core/audio';
import { TONE_MOOD, useMusicMood } from '../../../core/audio/music';
import { hotkeyFor } from '../../../core/input/hotkeys';

const PAGE_KEYS = ['story.next', 'story.back'] as const;

export interface IntroHero { key: string; id?: bigint; look?: HeroLook; name: string; frame?: number; classId?: string }

/** What the last page offers: take up arms, wait for friends, or (the battle is over) enter. */
export type CallState =
  | { kind: 'arms'; busy?: boolean; recruit?: boolean }
  | { kind: 'waiting'; on: string[]; keeper: boolean; busy?: boolean }
  /** docs/155 RF1: the battle began without me: join it as reinforcements, or stay in town. */
  | { kind: 'join'; busy?: boolean; recruit?: boolean }
  | { kind: 'enter'; label: string };

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** docs/109: how the new peoples are named as a warband and as an ambush. */
const COMPANY: Partial<Record<People, [string, string]>> = {
  Vampires: ['A vampire court', 'Vampires in the dark'], Ghosts: ['A haunting', 'The restless dead'],
  Mummies: ['A tomb dynasty risen', 'The wrapped dead'], Demons: ['A host of the pit', 'Demons out of the smoke'],
  Golems: ['An artificer and its golems', 'Golems on the road'], Elementals: ['Bound elementals', 'Spirits of the wild'],
  Djinn: ['A djinn and its spirits', 'A djinn on the wind'], Coven: ['A coven of witches', 'Witches in the hedges'],
  Mimics: ['A vault of mimics', 'Mimics in the loot'], Beholder: ['An eye tyrant', 'Eyes in the dark'],
  Gorgons: ['A gorgon and her serpents', 'Serpents in the ruins'], Minotaurs: ['A minotaur herd', 'Minotaurs in the halls'],
  Cyclopes: ['A band of hill giants', 'Giants on the road'], Drakes: ['A brood of drakes', 'Drakes out of the sky'],
  Vermin: ['A swarm of giant vermin', 'Vermin from below'], WinterCourt: ['The winter court', 'Frost in the night'],
  Fey: ['A fey court', 'Fey tricksters'], Duergar: ['A duergar war-band', 'Duergar from the deep'],
  MenAtArms: ['A tyrant\'s men-at-arms', 'Men-at-arms on the road'],
};
/** "An orc warband" / "Gnoll raiders" / "A burrowing beast": who the first battle is against (docs/46 OV1). */
function foeWord(b: Genesis['battle']): string {
  // docs/50: the brief's own cast, every folk it fields
  if (b.brief) return briefFoeWords(b.brief);
  const p = peopleOf(b);
  if (b.foe === 'Beast') return p === 'Spider' ? 'A great spider' : 'A burrowing beast';
  if (p === 'Dead') return b.foe === 'Warband' ? 'A host of the risen dead' : 'The risen dead';
  const company = COMPANY[p];
  if (company) return b.foe === 'Warband' ? company[0] : company[1];
  const word = PEOPLE_LOOK[p].word;
  return b.foe === 'Warband' ? `${/^[aeiou]/.test(word) ? 'An' : 'A'} ${word} warband` : `${cap(word)} raiders`;
}
/** The reveal's eyebrow over the named foe's name. */
function revealWord(b: Genesis['battle']): string {
  if (b.brief) {
    if (!b.brief.cast.squad.length) return b.brief.cast.leader.archetype === 'Lurker' ? 'Something old stirs below the ground' : 'Something huge comes out of the dark';
    return `${briefFoeWords(b.brief)}, and at their head`;
  }
  const p = peopleOf(b);
  if (b.foe === 'Beast') return p === 'Spider' ? 'Something old stirs in its burrow' : 'Something old wakes under the ground';
  if (p === 'Dead') return 'The dead rise, and behind them';
  return `${foeWord(b)}, and at ${b.foe === 'Warband' ? 'its' : 'their'} head`;
}
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
const GROUND_WORD: Record<string, string> = {
  Mire: 'the mire', Greenwood: 'the greenwood', Autumnwood: 'the autumn wood', Pinewood: 'the pinewood', Snowfield: 'the snowfields',
  Dunes: 'the dunes', Highland: 'the high moor', Jungle: 'the jungle', Underdark: 'the Underdark', Ashlands: 'the Ashlands',
};
const SKY_WORD: Record<string, string> = {
  Clear: 'clear skies', Overcast: 'grey skies', Rain: 'rain', Storm: 'a storm', Fog: 'fog', Snow: 'snow',
  Heat: 'scorching heat', Sandstorm: 'a sandstorm', Blizzard: 'a blizzard', Frost: 'hard frost', Sleet: 'sleet',
};

/** "Midnight · fog · the mire" — when and where the first battle is fought (docs/22 O3). */
export function stageLine(stage: Stage): string {
  const h = stage.hour;
  if (stage.ground === 'Underdark') {
    // docs/23 Y9: the glow's hours and the deep's weathers
    const glow = h >= 21 || h < 4 ? 'The deep dark' : h < 8 ? 'The waking glow' : h < 16 ? 'The full glow' : 'The fading glow';
    return `${glow} · ${DEEP_SKY_WORDS[stage.weather.toLowerCase()] ?? SKY_WORD[stage.weather] ?? stage.weather.toLowerCase()} · ${GROUND_WORD.Underdark}`;
  }
  if (stage.ground === 'Ashlands') {
    // docs/23 Y10: the red day, the fire-dark and the mountains' weathers
    const when = h >= 21 || h < 4 ? 'The fire-dark' : h < 8 ? 'A blood-red dawn' : h < 17 ? 'The red day' : 'The ember dusk';
    return `${when} · ${ASH_SKY_WORDS[stage.weather.toLowerCase()] ?? SKY_WORD[stage.weather] ?? stage.weather.toLowerCase()} · ${GROUND_WORD.Ashlands}`;
  }
  const when = h === 0 ? 'Midnight' : h === 12 ? 'Noon' : h < 5 ? 'The small hours' : h < 8 ? 'Dawn' : h < 12 ? 'Morning' : h < 17 ? 'Afternoon' : h < 21 ? 'Dusk' : 'Night';
  return `${when} · ${SKY_WORD[stage.weather] ?? stage.weather.toLowerCase()} · ${GROUND_WORD[stage.ground] ?? stage.ground.toLowerCase()}`;
}

/** The speaker's seal: their initial in a gold lozenge (story people have no sprite). */
export const Seal = ({ name, foe }: { name: string; foe?: boolean }) => <span className={`fk-intro__seal ${foe ? 'fk-intro__seal--foe' : ''}`} aria-hidden="true"><b>{name.replace(/^(the|old|mother|father|sister|brother|reeve|captain|goody|lady|lord|master|mistress)\s+/i, '').charAt(0).toUpperCase()}</b></span>;

export function Typed({ text, onDone, onType, className, whole }: { text: string; onDone: (skip: () => void, done: boolean) => void; /** characters shown (docs/43 beats) */ onType?: (n: number) => void; className?: string; /** a page already read: shown whole */ whole?: boolean }) {
  const { shown, done, skip } = useTypewriter(text, 70, CINE_TYPE_GAIN);
  useEffect(() => { if (whole) skip(); }, [whole, skip]);
  useEffect(() => { onType?.(shown.length); }, [shown.length, onType]);
  useEffect(() => { onDone(skip, done); }, [done, skip, onDone]);
  return <p className={className}>{shown}{!done && <span className="fk-intro__cursor" aria-hidden="true">▌</span>}<span className="fk-visually-hidden">{text}</span></p>;
}

/** A spoken line in the library's dialogue box style, finished early from the page's own Next. */
export function SpokenLine({ speaker, text, onDone, onType, skipRef, foe, whole }: { speaker: string; text: string; onDone: () => void; onType?: (n: number) => void; skipRef: { current: () => void }; foe?: boolean; whole?: boolean }) {
  const { shown, done, skip } = useTypewriter(text, 60, CINE_TYPE_GAIN);
  useEffect(() => { if (!whole) playSfx('cine.line'); }, []);   // docs/44: the line slides in
  useEffect(() => { if (whole) skip(); }, [whole, skip]);
  useEffect(() => { onType?.(shown.length); }, [shown.length, onType]);
  useEffect(() => { skipRef.current = skip; if (done) onDone(); }, [done, skip, onDone, skipRef]);
  return (
    <div className={`fk-panel fk-panel--ornate fk-dialogue fk-dialogue--no-portrait fk-dialogue--clickable fk-intro__line ${foe ? 'fk-intro__line--foe' : ''}`} data-testid="intro-line" data-done={done} aria-label={`${speaker} says`}>
      <Seal name={speaker} foe={foe} />
      <span className="fk-dialogue__name">{speaker}</span>
      <p className="fk-dialogue__text fk-intro__line-text" aria-live="polite">{shown}{!done && <span className="fk-dialogue__cursor" aria-hidden="true">▌</span>}</p>
      {done && <span className="fk-dialogue__more" aria-hidden="true">▼</span>}
    </div>
  );
}

/**
 * The curtain between pages: `falling` to black, `down` while the next page swaps in and its scene
 * paints, `rising` as it fades up. Durations mirror --fk-dur-3 / --fk-dur-4 (0 under reduce-motion).
 */
type Curtain = 'up' | 'falling' | 'down' | 'rising';
const FALL_MS = 420, RISE_MS = 800, HOLD_MS = 900, FIRST_PAINT_MS = 2500;

/** The named foe steps out with the band at his back (docs/22 O4), on the battle's ground. */
function revealCast(g: Genesis): Cast {
  const frames = bandFrames(g.battle);
  const band = frames.length ? [{ cue: 'band', frames, side: 'foe' as const, n: 3 }] : [];
  return { cues: ['leader', ...(band.length ? ['band'] : [])], actors: [{ cue: 'leader', frames: [leaderFrame(g.battle)], side: 'foe', n: 1, speaker: true }, ...band], props: [], sky: {} };
}

/** The intro over a genesis; the live screen and the `?intro=1` harness both render it. */
export function IntroView({ g, heroes, call, onArms, onForce, onJoin, onSkipOpening, onEnter, startAt = 0 }: {
  g: Genesis; heroes: IntroHero[]; call: CallState;
  onArms: () => void; onForce?: () => void; onJoin?: () => void; onSkipOpening?: () => void; onEnter?: () => void; startAt?: number;
}) {
  const up = useLinkUp();
  const panels = g.intro;
  // docs/133 YA7: no title page; the realm's name rides over the first panel
  const reveal = panels.length;                // 0..n-1 panels, n the named foe, n+1 the call to arms
  const last = panels.length + 1;
  const first = Math.min(startAt, last);
  const [step, setStep] = useState(first);     // the page under the curtain
  const [aim, setAim] = useState(first);       // the page being turned to (pips and buttons follow it at once)
  const aimRef = useRef(first);
  // the cold open fades up from black once its first scene has painted
  const [curtain, setCurtain] = useState<Curtain>('down');
  const curtainRef = useRef<Curtain>('down');
  curtainRef.current = curtain;
  const timer = useRef(0);
  const seen = useRef(new Set<number>());
  // docs/44: each page sounds its moment once, as its curtain starts up (the realm's title, a chapter's
  // numeral, the named foe landing, the call to arms); a page turned back to stays quiet
  const sounded = useRef(new Set<number>());
  const stepRef = useRef(first);
  const [textDone, setTextDone] = useState(first === last);
  const [lineDone, setLineDone] = useState(false);
  const skipRef = useMemo(() => ({ current: () => {} }), []);
  const lineSkipRef = useMemo(() => ({ current: () => {} }), []);
  const names = heroes.map((h) => h.name);
  const panel = step < panels.length ? panels[step] : undefined;
  const scene: Scene = panel ? panel.scene : panels[panels.length - 1]?.scene ?? 'Fire';
  // the named foe and the call to arms stand on the battle's own ground, hour and weather (docs/22 O3)
  const stage = step >= reveal ? g.battle.stage : undefined;
  // the story lexicon: each page's words block its backdrop
  // docs/145: the heroes stand in the scene in their own kit (a token per look; a stand-in in its class's kit)
  const looks = useHeroLooks((s) => s.looks);
  const party = heroes.map((h) => heroToken(h.look ?? (h.id !== undefined ? looks[h.id.toString()] : undefined) ?? classLook(h.classId)));
  const partyKey = party.join(',');
  const casts = useMemo(() => {
    const ctx = storyContext(g, partyKey ? partyKey.split(',').map(Number) : []);
    return [...panels.map((p) => castPanel(p, ctx)), revealCast(g)];
  }, [g, panels, partyKey]);
  const cast = step <= reveal ? casts[step] : undefined;
  const castSeed = panel ? textSeed(panel.heading, panel.text) : textSeed(g.realm.name, String(step));

  const settle = (s: Curtain, ms: number, then?: () => void) => {
    window.clearTimeout(timer.current);
    setCurtain(s);
    if (then) timer.current = window.setTimeout(then, ms);
  };
  /** swap the page under the black: fresh typing (whole if already read), then wait for the paint */
  const commit = useCallback((to: number) => {
    const read = seen.current.has(to);
    playSfx('ui.page');   // docs/34: the page turns under the black
    playSfx('cine.page', { gain: 0.8 });   // docs/44: and the air moves with it
    skipRef.current = () => {}; lineSkipRef.current = () => {};
    setStep(to);
    stepRef.current = to;
    setTextDone(to === last || read);
    setLineDone(read);
    // never wait on a paint for ever (an atlas that will not load)
    settle('down', HOLD_MS, () => lift());
  }, [last, skipRef, lineSkipRef]);
  const lift = useCallback(() => {
    if (curtainRef.current !== 'down') return;
    curtainRef.current = 'rising';
    const at = stepRef.current;
    if (!sounded.current.has(at)) {
      sounded.current.add(at);
      playSfx(at === 0 ? 'cine.title' : at === reveal ? 'cine.reveal' : at === last ? 'cine.objective' : 'cine.chapter', { whenUnlocked: at === 0 });
    }
    settle('rising', motionMs(RISE_MS), () => setCurtain('up'));
  }, [reveal, last]);
  /** the new scene has painted: give the browser a frame to show it, then raise the curtain */
  const onPainted = useCallback(() => {
    if (curtainRef.current !== 'down') return;
    requestAnimationFrame(() => requestAnimationFrame(lift));
  }, [lift]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  // the cold open waits on its first scene's paint, but never for ever: art arriving slowly on a cold
  // start (or an atlas that will not load) still lets the curtain rise and the tale begin
  useEffect(() => { const t = window.setTimeout(() => lift(), FIRST_PAINT_MS); return () => window.clearTimeout(t); }, [lift]);
  // docs/34: the tale opens with its stinger (a replay from the Saga too)
  useEffect(() => { playSfx('ui.world_begins', { whenUnlocked: true }); }, []);
  // docs/44: the cold open's moments decoded ahead, so the first page turn and the rise are on time
  useEffect(() => { void preloadSfx(['cine.page', 'cine.title', 'cine.chapter', 'cine.line', 'cine.rise', 'cine.reveal', 'cine.objective'], true); }, []);
  const go = useCallback((to: number) => {
    const at = Math.max(0, Math.min(last, to));
    if (at === aimRef.current) return;
    // docs/44: dread rises as the curtain falls toward the named foe (not on the way back)
    if (at === reveal && at > aimRef.current && !sounded.current.has(at)) playSfx('cine.rise');
    aimRef.current = at;
    setAim(at);
    const c = curtainRef.current;
    if (c === 'falling') return;                 // already on its way down: it lands on the new aim
    if (c === 'down' || !motionMs(FALL_MS)) { commit(at); return; }
    curtainRef.current = 'falling';
    settle('falling', motionMs(FALL_MS), () => commit(aimRef.current));
  }, [last, reveal, commit]);

  const onTyped = useCallback((skip: () => void, done: boolean) => { skipRef.current = skip; if (done) setTextDone(true); }, [skipRef]);
  const needsLine = !!panel?.line;
  const leaderName = g.battle.leader.name.trim().toLowerCase();
  const markLine = useCallback(() => setLineDone(true), []);
  const complete = textDone && (!needsLine || lineDone);
  const turning = aim !== step || curtain === 'falling' || curtain === 'down';
  useEffect(() => { if (complete) seen.current.add(step); }, [complete, step]);
  const next = useCallback(() => {
    if (!turning) {
      if (!textDone) { skipRef.current(); return; }
      if (needsLine && !lineDone) { lineSkipRef.current(); return; }
    }
    go(aimRef.current + 1);
  }, [turning, textDone, needsLine, lineDone, go, skipRef, lineSkipRef]);
  const back = useCallback(() => go(aimRef.current - 1), [go]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.tagName === 'BUTTON')) return;
      if (useDialogs.getState().queue.length || document.querySelector('.fk-modal')) return;
      const key = hotkeyFor(e, PAGE_KEYS);
      if (key === 'story.next') { e.preventDefault(); if (aimRef.current < last) next(); }
      else if (key === 'story.back') { e.preventDefault(); back(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [next, back, last]);
  const read = seen.current.has(step);
  // docs/43: the page's beats land as its words appear (a page read before stays quiet)
  const [typedText, setTypedText] = useState(0);
  const [typedLine, setTypedLine] = useState(0);
  useEffect(() => { setTypedText(0); setTypedLine(0); }, [step]);
  useBeatScope(step);
  // docs/44: the score follows the World's tone, and darkens when the named foe steps out
  useMusicMood(step === reveal ? 'dread' : TONE_MOOD[g.realm.tone] ?? 'wonder', true);
  const pageText = panel ? withHeroes(panel.text, names) : step === reveal ? `\u201c${withHeroes(g.battle.cry, names)}\u201d` : '';
  const lineText = panel?.line ? withHeroes(panel.line.text, names) : '';
  const textBeats = useMemo(() => (panel ? beatsFor(g, 'Intro', step, pageText) : step === reveal ? beatsFor(g, 'Cry', 0, pageText) : []), [g, panel, step, reveal, pageText]);
  const lineBeats = useMemo(() => (panel ? beatsFor(g, 'Intro', step, lineText) : []), [g, panel, step, lineText]);
  useBeats(textBeats, typedText, !read && curtain !== 'down');
  useBeats(lineBeats, typedLine, !read && curtain !== 'down');

  const place = placeOf(g, g.battle.place);
  const figures = step === last && heroes.length > 0
    ? heroes.slice(0, 6).map((h, i) => <span key={h.key} className="fk-intro__figure" style={{ ['--i' as string]: i - (Math.min(heroes.length, 6) - 1) / 2 }}><HeroSprite heroId={h.id} look={h.id === undefined ? h.look ?? classLook(h.classId) : undefined} frame={h.frame} classId={h.classId} scale={3} title={h.name} breathe /><span className="fk-intro__figure-name">{h.name}</span></span>)
    : undefined;

  let body: ReactNode;
  if (panel) {
    // the spoken line sits outside the page: the page's centring transform would trap a fixed box
    body = (
      <>
        <div className="fk-intro__page" key={`p${step}`}>
          {step === 0 && (
            <div className="fk-intro__masthead" data-testid="intro-title">
              <span className="fk-intro__eyebrow">A new World is written</span>
              <h1 className="fk-intro__realm" data-testid="intro-realm">{display(g.realm.name)}</h1>
              <p className="fk-intro__epithet">{display(g.realm.epithet)}</p>
              {g.calendar && <p className="fk-intro__date" data-testid="intro-date">{storyDate(g.calendar)}</p>}
            </div>
          )}
          <span className="fk-intro__chapter" data-testid="intro-chapter">{ROMAN[step] ?? step + 1}</span>
          <h2 className="fk-intro__heading">{panel.heading}</h2>
          <Typed className="fk-intro__text" text={pageText} onDone={onTyped} onType={setTypedText} whole={read} />
        </div>
        {panel.line && textDone && (
          <SpokenLine key={`l${step}`} speaker={panel.line.speaker} text={lineText} onDone={markLine} onType={setTypedLine} skipRef={lineSkipRef} foe={panel.line.speaker.trim().toLowerCase() === leaderName} whole={read} />
        )}
      </>
    );
  } else if (step === reveal) {
    body = (
      <div className="fk-intro__reveal" key="reveal" data-testid="intro-named-foe">
        <span className="fk-intro__reveal-when">{stageLine(g.battle.stage)} · {display(place.name)}</span>
        <span className="fk-intro__reveal-eyebrow">{revealWord(g.battle)}</span>
        <h2 className="fk-intro__reveal-name" data-testid="intro-named-foe-name">{display(g.battle.leader.name)}</h2>
        <span className="fk-intro__reveal-title">{g.battle.leader.title}</span>
        <Typed className="fk-intro__reveal-cry" text={pageText} onDone={onTyped} onType={setTypedText} whole={read} />
      </div>
    );
  } else {
    body = (
      <div className="fk-intro__call" key="call">
        <div className="fk-panel fk-panel--ornate fk-intro__card" data-testid="intro-battle">
          <span className="fk-intro__card-eyebrow">The first battle</span>
          <h2 className="fk-intro__card-title">{g.battle.title}</h2>
          <p className="fk-intro__card-where">{display(place.name)} · {foeWord(g.battle)}</p>
          <p className="fk-intro__card-when" data-testid="intro-stage">{stageLine(g.battle.stage)}{g.calendar ? ` · ${storyDate(g.calendar)}` : ''}</p>
          <div className="fk-intro__foe">
            <span className="fk-intro__foe-name" data-testid="intro-leader">{display(g.battle.leader.name)}</span>
            <span className="fk-intro__foe-title">{g.battle.leader.title}</span>
          </div>
          <p className="fk-intro__objective"><span>Objective</span>{g.battle.objective}</p>
          <div className="fk-intro__actions">
            {call.kind === 'arms' && <Button primary size="lg" loading={call.busy} disabled={!up} title={up ? undefined : OFFLINE_TITLE} onClick={onArms} data-testid="intro-to-arms">{call.recruit ? 'Recruit your hero' : 'To arms'}</Button>}
            {call.kind === 'waiting' && <>
              <p className="fk-intro__waiting" data-testid="intro-waiting">{call.on.length ? `Waiting for ${call.on.join(', ')} to finish the tale…` : 'Sounding the horn…'}</p>
              {call.keeper && call.on.length > 0 && <>
                <Button variant="ghost" loading={call.busy} disabled={!up} onClick={onForce} data-testid="intro-force">Start the battle now</Button>
                <p className="fk-intro__hint" data-testid="intro-force-hint">Starts the first battle now, without waiting. Whoever is still reading can join it as reinforcements when they reach the end of the tale.</p>
              </>}
            </>}
            {call.kind === 'join' && <>
              <p className="fk-intro__hint" data-testid="intro-late">The first battle is already under way. Join it as reinforcements: your heroes come onto the field beside the fellowship, and the turn waits for you to plan.</p>
              <Button primary size="lg" loading={call.busy} disabled={!up} title={up ? undefined : OFFLINE_TITLE} onClick={onJoin} data-testid="intro-join">{call.recruit ? 'Recruit your hero' : 'Join the battle'}</Button>
              <Button variant="ghost" onClick={onEnter} data-testid="intro-enter">Stay in town</Button>
            </>}
            {call.kind === 'enter' && <Button primary size="lg" onClick={onEnter} data-testid="intro-enter">{call.label}</Button>}
          </div>
          {call.kind !== 'enter' && heroes.length > 0 && <p className="fk-intro__party">{heroes.length === 1 ? `${names[0]} answers the call.` : `${names.slice(0, 6).join(', ')} answer the call.`}</p>}
        </div>
      </div>
    );
  }

  return (
    <main className={`fk-intro ${step === reveal ? 'fk-intro--reveal' : ''}`} data-testid="intro-screen" data-step={step} data-steps={last + 1} data-curtain={curtain}>
      {/* a fresh camera per page, mounted under the black, so its drift starts as the curtain rises */}
      <div className={`fk-intro__camera fk-intro__camera--${step % 2 ? 'a' : 'b'}`} key={`cam${step}`}>
        <SceneBackdrop scene={scene} figures={figures} stage={stage} season={g.calendar?.season} biome={g.realm.biome} cast={cast} seed={castSeed} onPainted={onPainted} />
      </div>
      <SightLayer />
      <span className="fk-intro__letterbox fk-intro__letterbox--top" aria-hidden="true" />
      <span className="fk-intro__letterbox fk-intro__letterbox--bottom" aria-hidden="true" />
      <div className="fk-intro__stage fk-hit" data-testid="intro-panel" data-scene={scene} onClick={(e) => { if ((e.target as HTMLElement).closest('button, .fk-intro__card')) return; if (aimRef.current < last) next(); }}>
        {/* the page's words enter with the rising curtain (and leave under the falling one) */}
        {curtain !== 'down' && body}
      </div>
      <span className="fk-intro__curtain" data-state={curtain} aria-hidden="true" />
      <footer className="fk-intro__bar fk-hit">
        <Button small variant="ghost" disabled={aim === 0} onClick={back} data-testid="intro-back">‹ Back</Button>
        <ol className="fk-intro__pips" aria-label={`Page ${aim + 1} of ${last + 1}`}>
          {Array.from({ length: last + 1 }, (_, i) => <li key={i} className={i === aim ? 'on' : i < aim ? 'done' : ''} />)}
        </ol>
        <span className="fk-intro__spacer" />
        {onSkipOpening && call.kind !== 'enter' && <button type="button" className="fk-intro__link" onClick={onSkipOpening} data-testid="intro-skip-opening">Skip the opening</button>}
        {aim < last && <Button small variant="ghost" onClick={() => go(last)} data-testid="intro-skip">Skip intro »</Button>}
        {aim < last && <Button small primary={complete || turning} onClick={next} data-testid="intro-next">{complete || turning ? 'Next ›' : 'Finish line'}</Button>}
      </footer>
    </main>
  );
}

/** The live intro for the World I'm in (routed by RouteDriver, docs/21 §7). */
export function IntroScreen() {
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const go = useRoute((s) => s.go);
  const replay = useRoute((s) => s.replay);
  const g = useGenesis(worldId);
  const opening = useWorldOpening(worldId);
  const world = useWorld(worldId);
  const names = useNames(worldId);
  const presence = useWorldPresence(worldId);
  const members = useWorldMembers(worldId);
  const marks = useStoryMarks();
  const heroes = useMyHeroes().filter((h) => h.worldId === worldId && h.status === 0).sort((a, b) => (a.id < b.id ? -1 : 1));
  const [busy, setBusy] = useState(false);
  const [forging, setForging] = useState(false);
  const founding = useFounding(worldId);
  const [now, setNow] = useState(serverNow());
  useEffect(() => { const t = setInterval(() => setNow(serverNow()), 3000); return () => clearInterval(t); }, []);
  if (!worldId || !g) return <main className="fk-intro" data-testid="intro-screen" />;
  const keeper = identityHex(world?.owner) === me;
  const ready = (opening?.ready ?? []).map((r) => identityHex(r));
  const waiting = opening?.phase === OPENING.WAITING && ready.includes(me);
  const watching = presence.filter((p) => p.screen === 'intro' && (tsToMs(p.seenAt) ?? 0) > now - 30_000).map((p) => identityHex(p.owner));
  const on = [...new Set(watching)].filter((w) => !ready.includes(w)).map((w) => names[w] ?? w.slice(-6));
  const toWorld = () => { if (world) marks.setIntroSeen(world); go('world', { replay: undefined }); };
  const late = opening?.phase === OPENING.FIGHTING && !ready.includes(me) && !replay;
  const call: CallState = late ? { kind: 'join', busy, recruit: heroes.length === 0 }
    : !opening || opening.phase !== OPENING.WAITING
      ? { kind: 'enter', label: replay ? 'Back to the World' : `Enter ${display(g.realm.name)}` }
      : waiting ? { kind: 'waiting', on, keeper, busy } : { kind: 'arms', busy, recruit: heroes.length === 0 };
  const arms = async () => {
    if (heroes.length === 0) { setForging(true); return; }
    setBusy(true); await rpc.openingReady(worldId, false); setBusy(false);
  };
  const force = async () => { setBusy(true); await rpc.openingReady(worldId, true); setBusy(false); };
  // docs/155 RF1: into the running battle; the route follows the fellowship onto the board
  const join = async () => {
    if (heroes.length === 0) { setForging(true); return; }
    setBusy(true);
    const r = await rpc.joinOpening(worldId);
    setBusy(false);
    if (r.ok && world) marks.setIntroSeen(world);
  };
  const { recruit } = founding;
  return (
    <>
      <IntroView g={g} call={call} startAt={replay || waiting ? 0 : 0}
        heroes={heroes.map((h) => ({ key: h.id.toString(), id: h.id, name: h.name, frame: h.oryxFrame, classId: CLASS_NAMES[h.class] ?? 'fighter' }))}
        onArms={() => void arms()} onForce={() => void force()} onJoin={() => void join()} onEnter={toWorld}
        // in a shared World a player may leave the first battle to the others (I5); alone, it is the way in (ui/guided.ts)
        onSkipOpening={opening?.phase === OPENING.WAITING && members.length > 1 ? toWorld : undefined} />
      <Modal open={forging} onClose={() => setForging(false)} eyebrow={display(g.realm.name)} title="Recruit a hero" size="full" testId="forge-modal">
        <HeroForge worldId={worldId} rosterFull={!recruit.ok} fee={recruit.fee} capNote={recruit.why} onForged={() => setForging(false)} />
      </Modal>
    </>
  );
}
